import {
    formatUserFactorModel,
    normalizeFactorDataModel,
    normalizeUserFactorModel,
} from '../../../utils/factorDataOptions';
import { normalizeBetaSource } from '../../../utils/betaSources';
import { normalizeIdioVolSource } from '../dashboardUtils';

// Assemble the shared resource identity without changing the caller's already
// established risk context or target. Matrix cache revisions are not inputs.
export function buildPortfolioRiskOverviewScope({
    userUid,
    portfolioId,
    actualPortfolioVersion,
    watchlistVersion,
    targetKey = 'actual',
    factorModel,
    factorDataModel,
    factorSnapshotDate,
    betaSource,
    idioVolSource,
}) {
    return {
        userUid,
        portfolioId,
        portfolioRevision: JSON.stringify([actualPortfolioVersion, watchlistVersion]),
        targetKey,
        factorModel,
        factorDataModel,
        factorSnapshotDate,
        betaSource,
        idioVolSource,
    };
}

// The Dashboard's existing expected Actual scope, shared with the composed
// publication regression. Neither the committed payload nor its scope chooses
// this expectation: it is derived from current application state.
export function buildActualRiskOverviewScope({
    userUid,
    portfolioId,
    actualPortfolioVersion,
    watchlistVersion,
    factorModel,
    factorDataModel,
    factorSnapshotDate,
    betaSource,
    idioVolSource,
}) {
    return buildPortfolioRiskOverviewScope({
        userUid,
        portfolioId,
        actualPortfolioVersion,
        watchlistVersion,
        targetKey: 'actual',
        factorModel: formatUserFactorModel(factorModel),
        factorDataModel: normalizeFactorDataModel(
            normalizeUserFactorModel(factorModel),
            factorDataModel,
        ),
        factorSnapshotDate: factorSnapshotDate ? String(factorSnapshotDate).trim() : null,
        betaSource: normalizeBetaSource(betaSource),
        idioVolSource: normalizeIdioVolSource(idioVolSource),
    });
}

// UI resource versions are separate from the immutable matrix cache revision.
// Reserve no state: a plan can publish only against its captured baseline and
// exact publication owner. Its commit runs inside the core state transaction.
export function createPublicationVersionTransition({
    readVersions,
    commitVersions,
    isCurrent,
    actualSyncApplied = false,
}) {
    if (
        typeof readVersions !== 'function'
        || typeof commitVersions !== 'function'
        || typeof isCurrent !== 'function'
    ) {
        throw new Error('Publication version ownership is required.');
    }

    const baseline = { ...readVersions() };
    if (
        !Number.isSafeInteger(baseline.watchlistVersion)
        || baseline.watchlistVersion < 0
        || !Number.isSafeInteger(baseline.actualPortfolioVersion)
        || baseline.actualPortfolioVersion < 0
    ) {
        throw new Error('Publication version baseline is invalid.');
    }

    const target = Object.freeze({
        watchlistVersion: baseline.watchlistVersion + 1,
        actualPortfolioVersion: baseline.actualPortfolioVersion + (actualSyncApplied ? 1 : 0),
    });
    if (
        !Number.isSafeInteger(target.watchlistVersion)
        || !Number.isSafeInteger(target.actualPortfolioVersion)
    ) {
        throw new Error('Publication version target is invalid.');
    }

    let committed = false;
    const ownsVersions = () => {
        if (!isCurrent()) return false;
        const expected = committed ? target : baseline;
        const current = readVersions();
        return current.watchlistVersion === expected.watchlistVersion
            && current.actualPortfolioVersion === expected.actualPortfolioVersion;
    };

    return Object.freeze({
        ...target,
        isCurrent: ownsVersions,
        isCommitted: () => committed,
        commit: () => {
            if (!ownsVersions()) return false;
            if (committed) return true;
            commitVersions(target);
            committed = true;
            return true;
        },
    });
}
